import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    Output,
    ViewChild,
} from '@angular/core';

@Component({
    selector: 'ham-button',
    template: `
        <button
            #nativeButton
            class="ham-button"
            [type]="type || 'button'"
            [disabled]="disabled || isClicked"
            [class.active]="active"
            [class.inactive]="inactive"
            (click)="handleClick($event)"
        >
            <span class="icon" *ngIf="icon">
                <i [style]="iconStyle"></i>
            </span>
            <lucide-icon
                *ngIf="lucideIcon"
                class="lucide-icon"
                [name]="lucideIcon"
                [size]="lucideSize"
                aria-hidden="true"
            ></lucide-icon>
            <span class="text" *ngIf="!hideText"><ng-content></ng-content></span>
            <span class="icon" *ngIf="rightIcon">
                <i [style]="rightIconStyle"></i>
            </span>
        </button>
    `,
    styleUrls: ['./button.component.scss'],
})
export class HamButtonComponent {
    @ViewChild('nativeButton', { static: true }) nativeButton!: ElementRef;
    @Input() icon: string | null = null;
    @Input() rightIcon: string | null = null;
    /**
     * A Lucide icon name (e.g. `eye-off`), for icons `assets/hamIcons` does not have. Where the
     * folder has the icon, `icon` stays the way to show it. The name must be in the icon set the
     * components module registers.
     */
    @Input() lucideIcon: string | null = null;
    @Input() variant: 'primary' | 'gray' | 'red' | 'teal' | 'green' | 'white' | 'transparent' | 'glass' | 'plain' =
        'primary';
    /** `toolbar` has the height of a filter pill, so buttons and pills line up in one row. */
    @Input() size: 'xxs' | 'xs' | 'sm' | 'lg' | 'base' | 'toolbar' = 'base';
    @Input() outline: boolean = false;
    @Input() type: string | null;
    @Input() disabled: boolean = false;
    @Input() active: boolean = false; // Add active input property
    @Input() inactive: boolean = false; // Add inactive input property
    @Output() buttonClick = new EventEmitter<Event>(); // Custom output event
    @Input() hideText: boolean = false;

    @HostBinding('class') get hostClasses(): string {
        return `ham-button ${this.size} ${this.variant} ${this.getOutline}`;
    }

    get getOutline(): string {
        return this.outline ? 'outline' : '';
    }

    get iconStyle() {
        return this.icon ? { '-webkit-mask-image': `url(${this.icon})`, 'mask-image': `url(${this.icon})` } : {};
    }

    /** The Lucide icon follows the text size, like the mask icon does with `1em`. */
    get lucideSize(): number {
        if (this.size === 'lg') {
            return 16;
        }
        return this.size === 'xs' || this.size === 'xxs' ? 12 : 14;
    }

    get rightIconStyle() {
        return this.rightIcon
            ? { '-webkit-mask-image': `url(${this.rightIcon})`, 'mask-image': `url(${this.rightIcon})` }
            : {};
    }

    isClicked = false; // New property to track if the button was clicked

    constructor(private cdr: ChangeDetectorRef) {}

    handleClick(event: Event) {
        if (this.disabled || this.isClicked) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        this.isClicked = true; // Temporarily disable button
        this.buttonClick.emit(event); // Emit custom event when the button is clicked

        // Re-enable the button after a delay (e.g., 300 milliseconds)
        setTimeout(() => {
            this.isClicked = false;
            this.cdr.detectChanges(); // Manually trigger change detection
        }, 150); // You can adjust the debounce delay as needed
    }
}
